<template>
  <div class="evidence-recommendations-card gm-card rounded-xl pa-5 mb-4 border">
    <div class="d-flex flex-wrap align-center justify-space-between ga-2 mb-3">
      <div class="d-flex align-center ga-2">
        <div class="icon-circle bg-blue-50 d-flex align-center justify-center rounded-lg">
          <v-icon color="#2655f1" size="20">mdi-clipboard-list-outline</v-icon>
        </div>
        <div>
          <h3 class="text-subtitle-1 font-weight-bold text-slate-800 mb-0">
            处置规程 (SOP) 与专家建议
          </h3>
          <div class="text-caption text-slate-500">
            基线 SOP 规程置顶 · 严格禁止在复核前远程降级告警
          </div>
        </div>
      </div>

      <div v-if="confidenceText" class="text-caption bg-slate-50 px-2-5 py-1 rounded border">
        诊断置信度：<strong :class="confidenceColorClass">{{ confidenceText }}</strong>
      </div>
    </div>

    <!-- 待验证原因假设（若存在） -->
    <div v-if="hypotheses.length" class="hypotheses-box pa-3-5 rounded-xl mb-4 border d-flex align-start ga-3 bg-slate-50">
      <v-icon color="#64748b" size="18" class="mt-0-5 flex-shrink-0">mdi-head-snowflake-outline</v-icon>
      <div>
        <div class="text-caption font-weight-bold text-slate-700 mb-1">靶向原因假设排查指引</div>
        <div class="d-flex flex-column ga-1">
          <div v-for="(h, idx) in hypotheses" :key="idx" class="text-caption text-slate-600 d-flex align-start ga-1-5">
            <span class="dot-amber mt-1" />
            <span>{{ h }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 双路规程 SOP 建议卡片 -->
    <div class="d-flex flex-wrap ga-4">
      <!-- 1. 现场运维应急规程（最高优先级） -->
      <div class="rec-column flex-grow-1 flex-basis-0 pa-4 rounded-xl border bg-white">
        <div class="d-flex align-center justify-space-between pb-3 mb-3 border-b">
          <div class="d-flex align-center ga-2">
            <div class="sop-icon-box bg-orange-50 d-flex align-center justify-center rounded">
              <v-icon color="#ea580c" size="16">mdi-hard-hat</v-icon>
            </div>
            <div>
              <div class="font-weight-bold text-body-2 text-slate-800">现场运维处置 SOP</div>
              <div class="text-caption text-slate-400">现场复核规程</div>
            </div>
          </div>
          <span class="sop-tag text-caption px-2 py-0-5 rounded font-weight-bold bg-orange-50 text-orange-darken-3">
            应急处置
          </span>
        </div>

        <div v-if="displayedOpsList.length" class="rec-list d-flex flex-column ga-3">
          <div
            v-for="(item, idx) in displayedOpsList"
            :key="idx"
            class="rec-item pa-3 rounded-lg border d-flex align-start ga-3"
            :class="idx === 0 ? 'sop-item-first' : 'sop-item-normal'"
          >
            <div class="step-num flex-shrink-0 d-flex align-center justify-center rounded-full font-weight-bold">
              {{ idx + 1 }}
            </div>
            <div class="flex-grow-1">
              <div class="text-body-2 font-weight-medium text-slate-800 lh-base">
                {{ item }}
              </div>
              <div v-if="idx === 0" class="text-caption text-orange-darken-4 font-weight-bold mt-1">
                ★ 规则保底处置 SOP · 优先执行
              </div>
            </div>
          </div>

          <div v-if="opsRecommendations.length > INITIAL_SHOW_COUNT" class="text-center mt-1">
            <v-btn
              variant="text"
              density="compact"
              size="small"
              color="#2655f1"
              @click="opsExpanded = !opsExpanded"
            >
              {{ opsExpanded ? '收起增量建议' : `展开其余 ${opsRecommendations.length - INITIAL_SHOW_COUNT} 条现场建议` }}
            </v-btn>
          </div>
        </div>
        <div v-else class="text-caption text-disabled text-center py-6">
          暂无运维建议
        </div>
      </div>

      <!-- 2. 研发侧核验规程 -->
      <div class="rec-column flex-grow-1 flex-basis-0 pa-4 rounded-xl border bg-white">
        <div class="d-flex align-center justify-space-between pb-3 mb-3 border-b">
          <div class="d-flex align-center ga-2">
            <div class="sop-icon-box bg-blue-50 d-flex align-center justify-center rounded">
              <v-icon color="#2563eb" size="16">mdi-developer-board</v-icon>
            </div>
            <div>
              <div class="font-weight-bold text-body-2 text-slate-800">研发定位与排查建议</div>
              <div class="text-caption text-slate-400">端侧触发与防抖核对</div>
            </div>
          </div>
          <span class="sop-tag text-caption px-2 py-0-5 rounded font-weight-bold bg-blue-50 text-blue-darken-3">
            逻辑溯源
          </span>
        </div>

        <div v-if="displayedDevList.length" class="rec-list d-flex flex-column ga-3">
          <div
            v-for="(item, idx) in displayedDevList"
            :key="idx"
            class="rec-item pa-3 rounded-lg border d-flex align-start ga-3 bg-slate-50"
          >
            <div class="step-num-dev flex-shrink-0 d-flex align-center justify-center rounded-full font-weight-bold">
              {{ idx + 1 }}
            </div>
            <div class="flex-grow-1">
              <div class="text-body-2 text-slate-800 lh-base">
                {{ item }}
              </div>
              <div v-if="idx === 0" class="text-caption text-blue-darken-3 font-weight-medium mt-1">
                ★ 规则保底排查项 · 研发核查寄存器快照
              </div>
            </div>
          </div>

          <div v-if="devRecommendations.length > INITIAL_SHOW_COUNT" class="text-center mt-1">
            <v-btn
              variant="text"
              density="compact"
              size="small"
              color="#2655f1"
              @click="devExpanded = !devExpanded"
            >
              {{ devExpanded ? '收起增量建议' : `展开其余 ${devRecommendations.length - INITIAL_SHOW_COUNT} 条研发建议` }}
            </v-btn>
          </div>
        </div>
        <div v-else class="text-caption text-disabled text-center py-6">
          暂无研发建议
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue'

const props = defineProps({
  diagnosis: { type: Object, default: null },
  analysis: { type: Object, default: null },
})

const INITIAL_SHOW_COUNT = 3
const opsExpanded = ref(false)
const devExpanded = ref(false)

const hypotheses = computed(() => {
  return props.diagnosis?.pendingHypotheses || []
})

const opsRecommendations = computed(() => {
  return props.diagnosis?.opsRecommendations || []
})

const devRecommendations = computed(() => {
  return props.diagnosis?.devRecommendations || []
})

// 法定红线：折叠只能折叠尾部，规则前置 SOP 必须常显
const displayedOpsList = computed(() => {
  if (opsExpanded.value) return opsRecommendations.value
  return opsRecommendations.value.slice(0, INITIAL_SHOW_COUNT)
})

const displayedDevList = computed(() => {
  if (devExpanded.value) return devRecommendations.value
  return devRecommendations.value.slice(0, INITIAL_SHOW_COUNT)
})

const confidenceText = computed(() => {
  const c = props.diagnosis?.confidence
  switch (c) {
    case 'high':
      return '高置信 (High)'
    case 'medium':
      return '中置信 (Medium)'
    case 'low':
      return '低置信 (Low)'
    default:
      return c || '—'
  }
})

const confidenceColorClass = computed(() => {
  const c = props.diagnosis?.confidence
  if (c === 'high') return 'text-green-darken-3'
  if (c === 'medium') return 'text-amber-darken-3'
  return 'text-slate-500'
})
</script>

<style scoped lang="scss">
.evidence-recommendations-card {
  background: #ffffff;
  border-color: #e2e8f0;
  box-shadow: 0 2px 12px rgba(15, 23, 42, 0.04);
}

.icon-circle {
  width: 36px;
  height: 36px;
}

.sop-icon-box {
  width: 28px;
  height: 28px;
}

.border-b {
  border-bottom: 1px solid #f1f5f9;
}

.hypotheses-box {
  border-color: #e2e8f0;
}

.dot-amber {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #f59e0b;
  flex-shrink: 0;
}

.rec-column {
  border-color: #e2e8f0;
}

.rec-item {
  transition: all 0.2s;

  &.sop-item-first {
    background: #fffaf5;
    border-color: #ffedd5;
  }
  &.sop-item-normal {
    background: #ffffff;
    border-color: #f1f5f9;
  }
}

.step-num {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #ffedd5;
  color: #c2410c;
  font-size: 11px;
}

.step-num-dev {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #dbeafe;
  color: #1d4ed8;
  font-size: 11px;
}
</style>
